// =============================================================================
// Comparativa de planes con upsell. Precios en B/. (USD paridad). La matriz de
// límites es app.plan_limits (BD = fuente única); aquí sólo se renderizan.
// El cambio de plan real corre por Paddle (pendiente); el botón abre el flow.
// =============================================================================
import { money } from "../lib/utils";

export interface TierSpec {
  id: "free" | "pro" | "business" | "enterprise";
  label: string;
  tagline: string;
  price: number | null;          // null = "Contáctanos"
  highlight?: boolean;            // tier destacado
  // límites (espejo de app.plan_limits — fuente real: BD)
  branches: number | null;
  users: number | null;
  terminals: number | null;
  skus: number | null;
  docsMonth: number | null;
  features: string[];             // bullets de venta
}

export const TIERS: TierSpec[] = [
  {
    id: "free", label: "Gratis", tagline: "Para probar",
    price: 0, branches: 1, users: 1, terminals: 1, skus: 300, docsMonth: 100,
    features: [
      "1 sucursal · 1 caja · 1 usuario",
      "POS táctil con código de barras",
      "Inventario y kardex completos",
      "Reportes básicos",
      "Soporte por email",
    ],
  },
  {
    id: "pro", label: "Pro", tagline: "Ferretería única",
    price: 29, highlight: true,
    branches: 1, users: 3, terminals: 2, skus: 5000, docsMonth: 1000,
    features: [
      "3 usuarios · 2 cajas · 5,000 productos",
      "Factura electrónica DGI / SFEP (1,000 docs/mes)",
      "Yappy + tarjeta + fiado con cupo",
      "Cuentas por cobrar y aging",
      "Reportes de venta y márgenes",
    ],
  },
  {
    id: "business", label: "Business", tagline: "Multi-sucursal",
    price: 89,
    branches: 3, users: 8, terminals: 6, skus: 30000, docsMonth: 5000,
    features: [
      "3 sucursales · 6 cajas · 8 usuarios",
      "Transferencias entre sucursales",
      "GMROI, dead stock, ABC, reorden automático",
      "Notas de crédito y devoluciones",
      "Soporte prioritario",
    ],
  },
  {
    id: "enterprise", label: "Enterprise", tagline: "Cadena/franquicia",
    price: null,
    branches: null, users: null, terminals: null, skus: null, docsMonth: null,
    features: [
      "Sucursales/cajas/usuarios ilimitados",
      "API y webhooks dedicados",
      "Integraciones contables (Quickbooks, SAP)",
      "SLA 99.9% + onboarding asistido",
      "Tarifa por volumen",
    ],
  },
];

export function PlanTiers({ current, onPick }: { current: string; onPick?: (tierId: TierSpec["id"]) => void }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      {TIERS.map((t) => {
        const isCurrent = t.id === current;
        return (
          <div key={t.id}
            className={`relative flex flex-col rounded-2xl border p-5 shadow-sm transition ${
              t.highlight ? "border-brand bg-brand/5" : "bg-white"
            } ${isCurrent ? "ring-2 ring-brand" : ""}`}>
            {t.highlight && !isCurrent && (
              <span className="absolute -top-3 left-4 rounded-full bg-brand px-3 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                Recomendado
              </span>
            )}
            <div className="mb-2">
              <h3 className="text-lg font-bold">{t.label}</h3>
              <p className="text-xs text-grafito-400">{t.tagline}</p>
            </div>
            <div className="mb-4">
              {t.price === null ? (
                <div className="text-2xl font-extrabold">A medida</div>
              ) : t.price === 0 ? (
                <div className="text-3xl font-extrabold">Gratis</div>
              ) : (
                <div>
                  <span className="text-3xl font-extrabold">{money(t.price)}</span>
                  <span className="ml-1 text-sm text-grafito-400">/mes</span>
                </div>
              )}
            </div>
            <ul className="mb-5 flex-1 space-y-1.5 text-sm">
              {t.features.map((f) => (
                <li key={f} className="flex gap-2"><span className="text-brand">✓</span><span>{f}</span></li>
              ))}
            </ul>
            <button
              disabled={isCurrent}
              onClick={() => onPick?.(t.id)}
              className={`mt-auto w-full rounded-lg px-3 py-2 text-sm font-semibold transition ${
                isCurrent
                  ? "cursor-default bg-grafito-100 text-grafito-500"
                  : t.id === "enterprise"
                    ? "bg-grafito-800 text-white hover:bg-grafito-900"
                    : "bg-brand text-white hover:bg-brand/90"
              }`}>
              {isCurrent ? "Plan actual" : t.id === "enterprise" ? "Contáctanos" : t.price && t.price > 0 ? "Cambiar a " + t.label : "Empezar gratis"}
            </button>
          </div>
        );
      })}
    </div>
  );
}
